/**
 * Instance-wide localisation, fed from the shared `settings.locale` Inertia
 * prop.
 *
 * These are module-level rather than a composable because formatDate() is
 * called from plain functions and table column definitions, not only from
 * component setup, so usePage() is not available at the call site. The values
 * are per-instance and change only when an operator saves the settings page, so
 * a module store kept in sync on navigation is enough — no reactivity needed.
 *
 * Product name, logos and author are NOT here: they are read through
 * useBranding(), because an operator can change them and a constant would go
 * stale the moment they did.
 */
const DEFAULT_LOCALE = {
    currencySymbol: 'R',
    timezone: 'Africa/Johannesburg',
}

const locale = { ...DEFAULT_LOCALE }

/**
 * Called from app.js on boot and on every Inertia navigation, and from ssr.js
 * once per render.
 *
 * Always assigns, never merges: under SSR one bundle serves every instance, so
 * a missing field must fall back to the shipped default rather than keep the
 * previous render's value.
 */
export function setInstanceLocale(next) {
    locale.currencySymbol = next?.currencySymbol || DEFAULT_LOCALE.currencySymbol
    locale.timezone = next?.timezone || DEFAULT_LOCALE.timezone
}

export function currencySymbol() {
    return locale.currencySymbol
}

export function instanceTimezone() {
    return locale.timezone
}

/**
 * Prefix an amount with the instance's currency symbol.
 */
export function formatCurrency(amount) {
    if (amount === null || amount === undefined || amount === '') return '—'

    const value = Number(amount)
    if (Number.isNaN(value)) return '—'

    return locale.currencySymbol + value.toFixed(2)
}

export const currentYear = new Date().getFullYear()

/**
 * Format a UTC date string for display in the instance's timezone.
 * Returns just the date (no time conversion drift).
 */
export function formatDate(dateStr) {
    if (!dateStr) return '—'
    return new Date(dateStr).toLocaleDateString('en-ZA', {
        timeZone: locale.timezone,
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    })
}

/**
 * Format a UTC date string with time for display in the instance's timezone.
 */
export function formatDateTime(dateStr) {
    if (!dateStr) return '—'
    return new Date(dateStr).toLocaleDateString('en-ZA', {
        timeZone: locale.timezone,
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

/**
 * Format a moment as a clock time in the instance's timezone. For "updated at"
 * stamps, where the date is always today and only the time carries meaning.
 */
export function formatTime(date) {
    if (!date) return '—'
    return new Date(date).toLocaleTimeString('en-ZA', {
        timeZone: locale.timezone,
        hour: '2-digit',
        minute: '2-digit',
    })
}

export const basePath = ''
